import {
  Card,
  MessageBar,
  MessageBarBody,
  makeStyles,
  Spinner,
  Text,
  tokens,
} from "@fluentui/react-components"
import type { MetricsKpis, MetricsResponse } from "@rema/shared/schemas/metrics"
import { useQuery } from "@tanstack/react-query"
import { EChartsHistogram } from "../../components/echarts-histogram"
import { PageHeader } from "../../components/page-header"
import { api, isApiError } from "../../lib/api"
import { problemMessage } from "../../lib/problem"
import { useTheme } from "../../theme/theme-provider"
import { dashboardI18n as i18n } from "./i18n"

const REFRESH_MS = 2000

const useStyles = makeStyles({
  grid: {
    display: "grid",
    // Exactamente 3 columnas; minmax(0,1fr) evita que una tarjeta desborde su celda.
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalL,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
  },
  cardTop: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  cardTitle: { color: tokens.colorNeutralForeground3 },
  cardValue: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold },
})

// Las 7 tarjetas del panel: cada una mapea una serie del snapshot a un histograma y
// extrae su valor "ahora" del bloque kpis. Los colores son de la paleta Fluent 2.
interface CardDef {
  metric: string
  title: string
  color: string
  unit?: string
  value: (k: MetricsKpis) => number
  decimals?: number
}

const CARDS: CardDef[] = [
  {
    metric: "gauge.active_users",
    title: i18n.cards.activeUsers,
    color: "#0F6CBD",
    value: (k) => k.active_users,
  },
  {
    metric: "logins.attempts",
    title: i18n.cards.loginsPerMinute,
    color: "#5B8FF9",
    unit: "/min",
    value: (k) => k.logins_per_minute + k.logins_failed_per_minute,
  },
  {
    metric: "logins.success",
    title: i18n.cards.loginsSuccess,
    color: "#107C10",
    unit: "/min",
    value: (k) => k.logins_per_minute,
  },
  {
    metric: "logins.failed",
    title: i18n.cards.loginsFailed,
    color: "#D13438",
    unit: "/min",
    value: (k) => k.logins_failed_per_minute,
  },
  {
    metric: "requests.rps",
    title: i18n.cards.requestsPerSecond,
    color: "#8764B8",
    unit: "req/s",
    value: (k) => k.requests_per_second,
    decimals: 2,
  },
  {
    metric: "gauge.nodes_healthy",
    title: i18n.cards.nodesHealthy,
    color: "#0E7A0B",
    value: (k) => k.nodes_healthy,
  },
  {
    metric: "gauge.nodes_total",
    title: i18n.cards.nodesTotal,
    color: "#4894FE",
    value: (k) => k.nodes_total,
  },
]

const formatValue = (n: number, decimals = 0): string =>
  decimals > 0 ? n.toFixed(decimals) : String(Math.round(n))

export const DashboardPage = () => {
  const styles = useStyles()
  const { mode } = useTheme()
  const dark = mode === "dark"

  // Polling cada 2 s: cada snapshot trae KPIs + series (contadores Redis + Postgres).
  // El backend también expone SSE en /admin/metrics/stream para integraciones server-to-server.
  const query = useQuery({
    queryKey: ["admin", "metrics"],
    queryFn: () => api.get<MetricsResponse>("/admin/metrics"),
    refetchInterval: REFRESH_MS,
  })

  if (query.isPending) return <Spinner />
  if (query.isError) {
    return (
      <>
        <PageHeader title={i18n.title} description={i18n.description} />
        <MessageBar intent="error">
          <MessageBarBody>
            {isApiError(query.error) ? problemMessage(query.error.problem) : i18n.loadError}
          </MessageBarBody>
        </MessageBar>
      </>
    )
  }

  const { kpis, series } = query.data
  const byMetric = new Map(series.map((s) => [s.metric, s]))

  return (
    <>
      <PageHeader title={i18n.title} description={i18n.description} />
      <div className={styles.grid}>
        {CARDS.map((def) => {
          const s = byMetric.get(def.metric)
          const points = s?.points ?? []
          const interval = s?.interval_seconds ?? 60
          return (
            <Card key={def.metric} className={styles.card}>
              <div className={styles.cardTop}>
                <Text className={styles.cardTitle}>{def.title}</Text>
                <Text className={styles.cardValue}>
                  {formatValue(def.value(kpis), def.decimals)}
                  {def.unit ? <Text size={300}> {def.unit}</Text> : null}
                </Text>
              </div>
              <EChartsHistogram
                points={points}
                intervalSeconds={interval}
                color={def.color}
                dark={dark}
                unit={def.unit}
              />
            </Card>
          )
        })}
      </div>
    </>
  )
}
